/**
 * sl-opil.7.3 + sl-k78j.6.2 + sl-oee3: Credit Purchase Section
 *
 * Shows subscription credit packs from the API:
 * - SUBSCRIPTION: Monthly recurring plans (individual checkout per card)
 *
 * TOP_UP and unknown catalog types remain available to backend callers but are
 * intentionally not rendered in Workspace Plans.
 *
 * Uses shared pricing card styles for visual consistency with plan cards.
 * Only visible when managed AI is enabled for the workspace.
 */

import { notify } from '@affine/component';
import {
  buildLabeledPurchaseOrderSummary,
  PurchaseConfirmDialog,
} from '@affine/core/components/consent';
import { WorkspaceSubscriptionService } from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { formatPrice } from '@affine/core/utils/format-price';
import type { WorkspaceCreditSubscriptionsQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useState } from 'react';

type ActiveCreditSubscription =
  WorkspaceCreditSubscriptionsQuery['workspaceCreditSubscriptions'][number];

import * as styles from '../../../../../components/pricing/pricing-cards.css';
import { useCreditPacks } from './use-credit-balance';
import {
  useCancelCreditSubscription,
  useCreateCreditCheckout,
  useManagedAIStatus,
  useWorkspaceCreditSubscriptions,
} from './use-managed-ai';

// ─── Types ──────────────────────────────────────────────────────────────────

interface CreditPackItem {
  id: string;
  name: string;
  credits: number;
  stripeProductId: string | null;
  packType: string;
  unitAmount: number | null;
  currency: string | null;
  interval: string | null;
}

type SubscriptionCreditPack = CreditPackItem & {
  packType: 'SUBSCRIPTION';
};

const isSubscriptionCreditPack = (
  pack: CreditPackItem
): pack is SubscriptionCreditPack => pack.packType === 'SUBSCRIPTION';

// ─── Credit Pack Card ───────────────────────────────────────────────────────

function CreditPackCard({
  pack,
  onCheckout,
  isLoading,
  locale,
}: {
  pack: SubscriptionCreditPack;
  onCheckout: (pack: SubscriptionCreditPack) => void;
  isLoading: boolean;
  locale: string;
}) {
  const t = useI18n();

  const priceStr =
    pack.unitAmount != null && pack.currency
      ? formatPrice(pack.unitAmount, pack.currency, locale)
      : pack.name;

  return (
    <div className={styles.pricingCard}>
      <div className={styles.pricingCardHeader}>
        <div className={styles.pricingCardName}>
          {t['com.streamlain.payment.addon.credits.credits']({
            count: String(pack.credits),
          })}
        </div>
        <div className={styles.pricingCardDescription}>
          {t['com.streamlain.payment.addon.credits.description']()}
        </div>
      </div>

      <div className={styles.pricingCardPriceSection}>
        <div className={styles.pricingCardPrice}>
          {priceStr}
          {pack.interval && (
            <span className={styles.pricingCardPriceInterval}>
              {t['com.streamlain.payment.addon.credits.per-month']()}
            </span>
          )}
        </div>
      </div>

      <button
        className={styles.pricingCardCta}
        data-featured={true}
        data-testid="credit-pack-subscription-cta"
        disabled={isLoading}
        onClick={() => onCheckout(pack)}
      >
        {isLoading
          ? t['com.streamlain.payment.addon.credits.redirecting']()
          : t['com.streamlain.payment.addon.credits.subscribe']()}
      </button>
    </div>
  );
}

// ─── Active Credit Subscriptions ─────────────────────────────────────────────

function ActiveCreditSubscriptions({
  workspaceId,
  workspaceFlavour,
  locale,
  subscriptions,
  refresh,
}: {
  workspaceId: string;
  workspaceFlavour: string;
  locale: string;
  subscriptions: ActiveCreditSubscription[];
  refresh: () => Promise<unknown>;
}) {
  const t = useI18n();
  const { cancelSubscription, isLoading: isCancelling } =
    useCancelCreditSubscription(workspaceFlavour);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);

  const handleCancel = useCallback(
    async (creditPackId: string) => {
      setCancelError(null);
      try {
        await cancelSubscription(workspaceId, creditPackId);
        setConfirmCancelId(null);
        await refresh();
      } catch (err) {
        setCancelError(
          err instanceof Error
            ? err.message
            : t['com.streamlain.payment.addon.credits.cancel-failed']()
        );
      }
    },
    [workspaceId, cancelSubscription, refresh, t]
  );

  if (subscriptions.length === 0) {
    return null;
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <>
      <div className={styles.addonSectionTitle}>
        {t['com.streamlain.payment.addon.credits.active-subscriptions']()}
      </div>
      <div className={styles.pricingCardsGrid}>
        {subscriptions.map((sub: ActiveCreditSubscription) => {
          const isYearly = sub.billingInterval === 'year';
          const intervalLabel = isYearly
            ? t['com.streamlain.payment.pricing.per-year']()
            : t['com.streamlain.payment.addon.credits.per-month']();

          return (
            <div key={sub.id} className={styles.pricingCard}>
              <div className={styles.pricingCardHeader}>
                <div className={styles.pricingCardName}>
                  {t['com.streamlain.payment.addon.credits.credits']({
                    count: String(sub.credits),
                  })}
                </div>
                <div className={styles.pricingCardDescription}>
                  {sub.cancelAtPeriodEnd
                    ? t[
                        'com.streamlain.payment.addon.credits.cancelled-at-period-end'
                      ]({ date: formatDate(sub.currentPeriodEnd) })
                    : intervalLabel}
                </div>
              </div>

              {!sub.cancelAtPeriodEnd && (
                <div className={styles.pricingCardPriceSection}>
                  {confirmCancelId === sub.creditPackId ? (
                    <div>
                      <div className={styles.pricingCardDescription}>
                        {t[
                          'com.streamlain.payment.addon.credits.cancel-confirm'
                        ]({ date: formatDate(sub.currentPeriodEnd) })}
                      </div>
                      <button
                        className={styles.pricingCardCta}
                        data-featured={false}
                        disabled={isCancelling}
                        onClick={() => void handleCancel(sub.creditPackId)}
                      >
                        {isCancelling
                          ? t[
                              'com.streamlain.payment.addon.credits.cancelling'
                            ]()
                          : t['com.streamlain.payment.addon.credits.cancel']()}
                      </button>
                    </div>
                  ) : (
                    <button
                      className={styles.pricingCardCta}
                      data-featured={false}
                      onClick={() => setConfirmCancelId(sub.creditPackId)}
                    >
                      {t['com.streamlain.payment.addon.credits.cancel']()}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {cancelError && (
        <div className={styles.addonCheckoutError}>{cancelError}</div>
      )}
    </>
  );
}

// ─── Subscription-only purchase content ─────────────────────────────────────

function CreditPurchaseSectionContent({
  workspaceId,
  workspaceFlavour,
  packs,
}: {
  workspaceId: string;
  workspaceFlavour: string;
  packs: CreditPackItem[];
}) {
  const t = useI18n();
  const { createCheckout } = useCreateCreditCheckout(workspaceFlavour);
  const { subscriptions, refresh } = useWorkspaceCreditSubscriptions(
    workspaceId,
    workspaceFlavour
  );

  const [buyingPackId, setBuyingPackId] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // §312j/§356 BGB: Confirmation dialog state
  const [pendingPack, setPendingPack] = useState<SubscriptionCreditPack | null>(
    null
  );

  const lang = t.language?.startsWith('en') ? 'en' : 'de';
  const locale = lang === 'en' ? 'en-US' : 'de-DE';

  const executeCheckout = useCallback(
    async (packId: string) => {
      setBuyingPackId(packId);
      setCheckoutError(null);

      try {
        const successUrl = `${window.location.origin}${window.location.pathname}?checkout=success`;
        const cancelUrl = `${window.location.origin}${window.location.pathname}?checkout=cancelled`;

        const result = await createCheckout(
          workspaceId,
          packId,
          successUrl,
          cancelUrl,
          true // withdrawalConsent
        );

        const url =
          (result as { createCreditCheckout?: string })?.createCreditCheckout ??
          (result as unknown as string);
        if (typeof url === 'string' && url.startsWith('http')) {
          window.location.href = url;
        } else {
          setCheckoutError(
            t['com.streamlain.payment.addon.credits.checkout-failed']()
          );
        }
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t['com.streamlain.payment.addon.credits.checkout-failed']();
        setCheckoutError(message);
        notify.error({
          title: t['com.streamlain.payment.addon.credits.checkout-failed'](),
          message,
        });
      } finally {
        setBuyingPackId(null);
      }
    },
    [workspaceId, createCheckout, t]
  );

  // §312j BGB: Open confirmation dialog instead of direct checkout.
  // This accepts only a card-rendered subscription pack, so TOP_UP and unknown
  // catalog records cannot reach pending state or checkout from this surface.
  const handleCheckout = useCallback((pack: SubscriptionCreditPack) => {
    setPendingPack(pack);
  }, []);

  const handleConfirmPurchase = useCallback(() => {
    if (pendingPack) {
      setPendingPack(null);
      void executeCheckout(pendingPack.id);
    }
  }, [pendingPack, executeCheckout]);

  const subscriptionPacks = packs.filter(isSubscriptionCreditPack);

  if (subscriptionPacks.length === 0 && subscriptions.length === 0) {
    return null;
  }

  return (
    <div data-testid="addon-section-credits" className={styles.addonSection}>
      <div
        data-testid="addon-section-title"
        className={styles.addonSectionTitle}
      >
        {t['com.streamlain.payment.addon.credits.title']()}
      </div>

      <ActiveCreditSubscriptions
        workspaceId={workspaceId}
        workspaceFlavour={workspaceFlavour}
        locale={locale}
        subscriptions={subscriptions}
        refresh={refresh}
      />

      {subscriptionPacks.length > 0 && (
        <div className={styles.pricingCardsGrid}>
          {subscriptionPacks.map(pack => (
            <CreditPackCard
              key={pack.id}
              pack={pack}
              onCheckout={handleCheckout}
              isLoading={buyingPackId === pack.id}
              locale={locale}
            />
          ))}
        </div>
      )}

      {/* §312j/§356 BGB: Per-transaction consent via confirmation dialog */}
      <PurchaseConfirmDialog
        open={pendingPack !== null}
        onOpenChange={open => {
          if (!open) setPendingPack(null);
        }}
        productName={
          pendingPack
            ? t['com.streamlain.payment.addon.credits.credits']({
                count: String(pendingPack.credits),
              })
            : ''
        }
        priceLabel={
          pendingPack?.unitAmount != null && pendingPack.currency
            ? `${formatPrice(pendingPack.unitAmount, pendingPack.currency, locale)}${t['com.streamlain.payment.addon.credits.per-month']()}`
            : ''
        }
        orderSummary={
          pendingPack?.unitAmount != null && pendingPack.currency
            ? buildLabeledPurchaseOrderSummary({
                consentMode: 'service',
                isSubscription: true,
                language: lang,
                productName: t['com.streamlain.payment.addon.credits.credits']({
                  count: String(pendingPack.credits),
                }),
                totalPriceLabel: `${formatPrice(pendingPack.unitAmount, pendingPack.currency, locale)}${t['com.streamlain.payment.addon.credits.per-month']()}`,
              })
            : undefined
        }
        isSubscription={true}
        consentMode="service"
        isLoading={buyingPackId !== null}
        onConfirm={handleConfirmPurchase}
      />

      {checkoutError && (
        <div className={styles.addonCheckoutError}>{checkoutError}</div>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export const CreditPurchaseSection = () => {
  const workspace = useService(WorkspaceService).workspace;
  const wsSubService = useService(WorkspaceSubscriptionService);
  const wsSub = useLiveData(wsSubService.subscription.subscription$);

  // sl-9kbi.2.2: Hide credit packs for Free plan users (backend also rejects, but UX first)
  const isFreePlan = !wsSub || wsSub.plan === 'Free';

  // Managed AI is the only mode — no toggle, no activation flow.
  // The status query triggers lazy-init (auto-provisioning with enabled=true).
  const { isLoading: statusLoading } = useManagedAIStatus(
    workspace.id,
    workspace.flavour
  );
  const { packs, isLoading: packsLoading } = useCreditPacks();

  if (statusLoading || packsLoading || isFreePlan) {
    return null;
  }

  return (
    <CreditPurchaseSectionContent
      workspaceId={workspace.id}
      workspaceFlavour={workspace.flavour}
      packs={packs as CreditPackItem[]}
    />
  );
};
